import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import Header from "./components/Header";
import {
  ProjectDetailsSection,
  FacilityMetricsSection,
  CapExSection,
  HelvarUpgradesSection,
  ExecutiveSummarySection,
} from "./components/InputsPanel";
import ResultsDashboard from "./components/ResultsDashboard";
import { calculateAll, defaultUpgradesState, DEFAULT_CO2_PER_KWH } from "./lib/calculations";
import { generateBusinessCasePdf } from "./lib/pdfExport";
import { readUtmParams, sendLead } from "./lib/leadCapture";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { I18nProvider, useI18n } from "./lib/i18n";
import { Megaphone } from "lucide-react";

const initialState = {
  clientName: "",
  projectName: "",
  projectDate: new Date().toISOString().slice(0, 10),
  contactEmail: "",
  company: "",
  jobTitle: "",
  gdprConsent: false,
  projectType: "renovation",
  compareCompetitor: false,
  existingLuminaires: 540,
  luminaires: 540,
  burningHoursPerWeek: 72,
  existingWattage: 60,
  proposedWattage: 30,
  existingRuntimePct: 100,
  upgradeRuntimePct: 85,
  pricePerKwh: 0.25,
  co2PerKwh: DEFAULT_CO2_PER_KWH,
  installationCost: 35000,
  luminaireCost: 95000,
  competitorControlsCost: 20000,
  helvarSystemCost: 48000,
  upgrades: defaultUpgradesState(),
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const StepDivider = ({ label, testId }) => (
  <div className="flex items-center gap-4 my-4" data-testid={testId}>
    <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#94A3B8]">
      {label}
    </span>
    <div className="flex-1 border-t border-dashed border-[#E2E8F0]" />
  </div>
);

function AppShell() {
  const { t, locale, sanitizeForPdf, LOCALES } = useI18n();
  const [state, setState] = useState(initialState);
  const [currencyCode, setCurrencyCode] = useState("EUR");
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const utmRef = useRef({});

  useEffect(() => {
    utmRef.current = readUtmParams();
  }, []);

  // When the user picks a language, auto-switch the currency to that
  // market's natural default (sv → SEK, pl → PLN, no → NOK, da → DKK,
  // fi/it/fr → EUR). English keeps whatever the user last chose so UK
  // reps can freely pick GBP.
  useEffect(() => {
    const meta = LOCALES.find((l) => l.code === locale);
    if (meta && meta.defaultCurrency) {
      setCurrencyCode(meta.defaultCurrency);
    }
  }, [locale, LOCALES]);

  const results = useMemo(() => calculateAll(state), [state]);
  const isEmailValid = EMAIL_REGEX.test((state.contactEmail || "").trim());
  const hasGdprConsent = !!state.gdprConsent;
  const canDownloadPdf = isEmailValid && hasGdprConsent;

  const handleDownloadPdf = async () => {
    if (!isEmailValid) {
      toast.error(t("toast.email_required.title"), {
        description: t("toast.email_required.desc"),
      });
      return;
    }
    if (!hasGdprConsent) {
      toast.error(t("toast.consent_required.title"), {
        description: t("toast.consent_required.desc"),
      });
      return;
    }
    try {
      setPdfLoading(true);
      await generateBusinessCasePdf({
        state,
        results,
        currencyCode,
        executiveSummary,
        t,
        locale,
        sanitizeForPdf,
      });
      sendLead({
        state,
        results,
        currencyCode,
        executiveSummary,
        utm: utmRef.current,
      });
      toast.success(t("toast.pdf_success.title"), {
        description: t("toast.pdf_success.desc"),
        duration: 6000,
      });
    } catch (err) {
      console.error(err);
      toast.error(t("toast.pdf_error.title"), {
        description: err?.message || "Unknown error",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="App min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      <div
        className="w-full bg-[#C80F3F] text-white text-[12px] md:text-[13px] leading-snug"
        data-testid="announcement-bar"
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-2.5 flex items-center justify-center gap-2.5 text-center">
          <Megaphone className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t("announcement.lead")}{" "}
            <strong className="font-semibold">{t("announcement.highlight")}</strong>
            {t("announcement.middle")}{" "}
            <a
              href="mailto:marketing@helvar.com?subject=ROI%20Calculator%20Feedback"
              className="font-semibold underline underline-offset-2 hover:text-white/80 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-sm"
              data-testid="announcement-email-link"
            >
              {t("announcement.cta")}
            </a>
            {t("announcement.tail")}
          </span>
        </div>
      </div>
      <Header />
      <main
        id="app-shell"
        className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12"
      >
        <div className="mb-2" data-testid="page-hero">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0F172A]">
            {t("hero.title")}
          </h1>
          <p className="mt-2 text-sm text-[#64748B] max-w-2xl">
            {t("hero.subtitle")}
            {state.clientName ? (
              <>
                {" "}
                {t("hero.for")}{" "}
                <span className="font-semibold text-[#0F172A]">{state.clientName}</span>
              </>
            ) : null}
            {state.projectName ? (
              <>
                {" "}·{" "}
                <span className="font-semibold text-[#0F172A]">{state.projectName}</span>
              </>
            ) : null}
          </p>
        </div>

        <StepDivider label={t("steps.inputs")} testId="divider-step-1" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-testid="row-inputs-1">
          <ProjectDetailsSection state={state} setState={setState} />
          <FacilityMetricsSection
            state={state}
            setState={setState}
            currencyCode={currencyCode}
            setCurrencyCode={setCurrencyCode}
          />
        </div>

        <StepDivider label={t("steps.budget")} testId="divider-step-2" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start" data-testid="row-inputs-2">
          <CapExSection state={state} setState={setState} currencyCode={currencyCode} />
          <HelvarUpgradesSection state={state} setState={setState} />
        </div>

        <StepDivider label={t("steps.results")} testId="divider-step-3" />
        <div data-testid="row-results">
          <ResultsDashboard
            results={results}
            state={state}
            currencyCode={currencyCode}
            onDownloadPdf={handleDownloadPdf}
            pdfLoading={pdfLoading}
            isEmailValid={isEmailValid}
            hasGdprConsent={hasGdprConsent}
            canDownloadPdf={canDownloadPdf}
          />
        </div>

        <StepDivider label={t("steps.summary")} testId="divider-step-4" />
        <div data-testid="row-exec-summary">
          <ExecutiveSummarySection
            executiveSummary={executiveSummary}
            setExecutiveSummary={setExecutiveSummary}
            onDownloadPdf={handleDownloadPdf}
            pdfLoading={pdfLoading}
            isEmailValid={isEmailValid}
            hasGdprConsent={hasGdprConsent}
            canDownloadPdf={canDownloadPdf}
            state={state}
          />
        </div>

        <footer className="mt-16 pt-8 border-t border-[#E2E8F0] text-xs text-[#94A3B8] flex flex-col md:flex-row items-start md:items-center justify-between gap-2 no-print">
          <span>{t("footer.copyright")}</span>
          <span>{t("footer.disclaimer")}</span>
        </footer>
      </main>
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <AppShell />
    </I18nProvider>
  );
}

export default App;
